import {
    IBlueprintItemDetails,
    IRoleConnectionDetail,
    ITemplateDetailsSettings,
    TActionField,
    TBlueprintItemTemplate,
    TConnectionGroupDetails,
    TTemplateDetailsSettings,
    TTemplateFields,
    TTemplateSettingValue,
    TTemporarySettings
} from '../../api/blueprints/blueprint-details.types';
import { Action, ActionType, BlueprintField, BlueprintType } from '../../api/blueprints/blueprint.types';
import { ITimeZoneDTO } from '../../api/timezones.dao';
import { actionsByType, approvalTypes, blueprintFieldsMapper, defaultBooleanBlueprintValues, settingOptions } from '../../constants/blueprint-details.mapper.constants';
import { isTrackingFieldEnabled } from '../../tracking-service/tracking-values';
import { timezonesCodeLabelMap } from '../timezones-code-label-map';
import { DeepReadonly, TObject } from '../type-extensions';
import { IMeetingTemplateDTO } from '../../api/blueprints/meeting.template.types';
import { IEventBlueprintDTO } from '../../api/blueprints/event.blueprint.types';
import { attendeeLinkTypeOptions } from '../../constants/blueprint-labels.constants';
import { IAudioBridgeConnection, IConnectionTemplateConnectionGroupDTO, IConnectionTemplateDTO, IConnectionTemplateField, IKnovioConnection, IOtherConnection, IRoleConnectionInput, IZoomConnection, IZoomWebinarConnection, TConnection } from '../../api/blueprints/connection.template.types';
import { ConnectionType, KnovioLabel, WebinarAutoRecording } from '../../api/connections.dao';
import * as IoC from 'typescript-ioc';
import { IReactIntlProvider } from '../react-intl-provider';
import { ConnectionVersion, EnablementStatus } from '../../constants/common.constants';
import { DAYS_COUNT_BEFORE_EXPIRATION_TEXT } from '../../components/_blueprints/conections-templates/connections-template-form/components/conections-list/conections-list.texts';
import { EXPIRY_DAYS_DISPLAY_TEXT } from '../../components/_events/_manage-event/_meetings-list/_meeting-form/_meeting-expiry-date/meeting-expiry-date.texts';
import { ConfernceModeValues } from '../../components/_blueprints/conections-templates/connections-template-form/components/conections-list/audio-bridge-connection/connection-audio-bridge-form.viewmodel';
import { getConnectionTemplateItemLabelText } from './connections.utils';
import { fromRoleConnectionFieldValue, resolveRoleConnectionLabel } from '../../components/_blueprints/conections-templates/connections-template-form/connection-template-role-connections/role-connections.utils';
import { PODIUM_ATTENDEES_LABEL, PODIUM_CHAT_LABEL, PODIUM_PARTICIPANTS_LABEL, PODIUM_QUESTION_ANSWER_LABEL } from '../../components/_events/_manage-event/_connections/_meeting-connections-form/podium-form/podium-form.texts';

export function convertBlueprintTemplateIntoBlueprintDetails(
    blueprintType: BlueprintType,
    template: TBlueprintItemTemplate,
    timezones?: DeepReadonly<ITimeZoneDTO[]>,
    meetingTemplates?: IMeetingTemplateDTO[],
    connectionsTemplates?: IConnectionTemplateDTO[],
): IBlueprintItemDetails {
    let initialBlueprintItemDetails: IBlueprintItemDetails = {
        blueprintId: template.blueprintId,
        name: template.name,
        description: template.description,
        templateDetailsSettings: [],
    };
    const fields: TTemplateFields = JSON.parse(JSON.stringify(template.fields));

    let temporarySettings: TTemporarySettings = {};
    setDefaultFieldsIntoTemplate(blueprintType, fields);
    setActionsIntoTemplate(blueprintType, template.actions, fields);
    assignTemplatePropsToFields(template, fields);
    // @ts-ignore
    let templateConnections: TConnection[] = fields.find(f => f.name === 'connections')?.value;
    // @ts-ignore
    let templateConnectionGroups: IConnectionTemplateConnectionGroupDTO[] = fields.find(f => f.name === 'connectionGroups')?.value || [];

    fields.forEach(field => {
        const settingKey = blueprintFieldsMapper[blueprintType][field.name];
        let fieldValues: TTemplateSettingValue['settingValue'];
        const isValueBoolean = typeof field.value === 'boolean';
        const isFieldTimeZone = field.name === 'timeZone';
        const isFieldMeetingBlueprintId = field.name === 'meetingBlueprintId';
        const isFieldPassportConfig = field.name === 'passportConfig';
        const isConnections = field.name === 'connections';
        const isFieldConnectionsBlueprintId = field.name === 'connectionsBlueprintId';
        const isFieldIntegrationMeetingBlueprintId = field.name === 'integrationMeetingBlueprintId';
        const isConnectionGroups = field.name === 'connectionGroups';
        const isRoleConnections = field.name === 'roleConnections';
        const isFieldAutoRegistration = field.name === 'autoRegistrationEnabled';
        const isFieldDefultMeetingTemplateId = field.name === 'defaultMeetingBlueprintId';
        const isV2ConnectionsTemplate = template.connectionVersion === ConnectionVersion.V2;
        const isFieldExpiryDays = field.name === 'expiryDays';

        if (isConnections) {
            field.value = mapConnectionFields(field.value);
        }

        if (isConnectionGroups) {
            field.value = mapConnectionGroupsFields(field.value, templateConnections);
        }

        if (isRoleConnections) {
            const mappedRoles = mapRoleConnectionFields(
                field.value as IRoleConnectionInput[],
                templateConnections,
                templateConnectionGroups
            );
            if (!mappedRoles.length) {
                return;
            }
            field.value = mappedRoles;
        }

        if (isFieldAutoRegistration && !isV2ConnectionsTemplate) {
            return;
        }

        if (isFieldTimeZone) {
            const timezoneCode = timezones.find(timezone => timezone.code === field.value);
            const label = timezonesCodeLabelMap[timezoneCode?.code] ? timezonesCodeLabelMap[timezoneCode.code] : field.value;
            const timeFormat = timezoneCode ? `(GMT${timezoneCode.offset}) ${label}` : label;
            field.value = timeFormat;
        }

        if (isFieldMeetingBlueprintId) {
            const meetingTemplate = meetingTemplates.find(tpl => tpl.blueprintId === field.value);
            field.value = meetingTemplate?.name ?? field.value;
        }

        if (isFieldConnectionsBlueprintId) {
            const connectionsTemplate = connectionsTemplates?.find(tpl => tpl.blueprintId === field.value);
            field.value = connectionsTemplate?.name ?? field.value;
        }

        if (isFieldPassportConfig) {
            field.value = field.value.title;
        }

        if (isFieldIntegrationMeetingBlueprintId) {
            const integrationMeetingTemplate = meetingTemplates.find(tpl => tpl.blueprintId === field.value);
            field.value = integrationMeetingTemplate?.name ?? field.value;
        }

        if (isFieldDefultMeetingTemplateId) {
            const defultMeetingTemplateId = meetingTemplates.find(tpl => tpl.blueprintId === field.value);
            field.value = defultMeetingTemplateId
                ? `Enabled - ${defultMeetingTemplateId.name}`
                : 'Disabled'
            ;
        }

        if (isFieldExpiryDays) {
            const reactIntlProvider = IoC.Container.get(IReactIntlProvider);
            const intl = reactIntlProvider.getIntl();
            field.value = typeof field.value === 'number'
                ? intl.formatMessage(EXPIRY_DAYS_DISPLAY_TEXT, { days: field.value })
                : EnablementStatus.DISABLED_UPPERCASE;
        }

        fieldValues = isValueBoolean ? isTrackingFieldEnabled(field.value) : field.value;
        temporarySettings[settingKey] = fieldValues;
    });

    initialBlueprintItemDetails.templateDetailsSettings = convertTemporarySettingsInDetailsSettings(
        blueprintType,
        temporarySettings
    );

    return initialBlueprintItemDetails;
};

const convertTemporarySettingsInDetailsSettings = (
    blueprintType: BlueprintType,
    temporarySettings: TTemporarySettings
): TTemplateDetailsSettings => {
    let templateDetailsSettings: TTemplateDetailsSettings = [];
    settingOptions[blueprintType].forEach(settingOption => {
        let setting: ITemplateDetailsSettings = {
            title: settingOption.settingTitle,
            settingValue: []
        };

        settingOption.settingValues.forEach(value => {
            if (value in temporarySettings) {
                setting.settingValue.push({
                    title: value,
                    settingValue: temporarySettings[value],
                    custom: settingOption.custom,
                    component: settingOption.component
                });
            }
        });
        if (setting.settingValue.length) {
            templateDetailsSettings.push(setting);
        }
    });
    return templateDetailsSettings;
};

function setDefaultFieldsIntoTemplate(
    blueprintType: BlueprintType,
    fields: TTemplateFields
): void {
    // Order templates store values on orderTemplate, not as blueprint fields.
    if (blueprintType === BlueprintType.ORDER) {
        return;
    }
    defaultBooleanBlueprintValues[blueprintType].forEach(defaultField => {
        const isFieldIsInTemplates = fields.some(field => field.name === defaultField.fieldName);
        if (!isFieldIsInTemplates) {
            fields.push(defaultField.fieldValue);
        }
    });
};

function setActionsIntoTemplate(
    blueprintType: BlueprintType,
    actions: Action[] = [],
    fields: TTemplateFields,
) {
    actionsByType[blueprintType].map(actionType => {
        const isActionSelected = actions.some(action => action.type === actionType);
        const actionField: TActionField = {
            name: actionType,
            value: isActionSelected
        };
        // @ts-ignore
        fields.push(actionField);
    });
};

function assignTemplatePropsToFields(template: TBlueprintItemTemplate, fields: TTemplateFields): void {
    switch (template.type) {
        case BlueprintType.EVENT:
            assignEventBlueprintPropsToFields(template, fields);
            break;
        case BlueprintType.MEETING:
            assignSingleMeetingCustomPagePropsToFields(template, fields);
            break;
        case BlueprintType.CONNECTION:
            assignConnectionBlueprintPropsToFields(template, fields as unknown as IConnectionTemplateField[]);
            break;
        default:
            break;
    }
};

function assignConnectionBlueprintPropsToFields(template: IConnectionTemplateDTO, fields: IConnectionTemplateField[]): void {
    [
        mapFeaturedPropsToField
    ].forEach(mapper => {
        const field = mapper(template, fields);
        // @ts-ignore
        if (field) fields.push(field);
    });
};

function mapFeaturedPropsToField(_: IConnectionTemplateDTO, fields: IConnectionTemplateField[]): Partial<BlueprintField> {
    const isPodiumParticipantsEnabled = fields.find(f => f.name === 'podiumParticipantsEnabled')?.value;
    const isPodiumAttendeesEnabled = fields.find(f => f.name === 'podiumAttendeesEnabled')?.value;
    const isPodiumChatEnabled = fields.find(f => f.name === 'podiumChatEnabled')?.value;
    const isPodiumQuestionAnswerEnabled = fields.find(f => f.name === 'podiumQuestionAnswerEnabled')?.value;

    const features = [
        isPodiumParticipantsEnabled && PODIUM_PARTICIPANTS_LABEL.defaultMessage,
        isPodiumAttendeesEnabled && PODIUM_ATTENDEES_LABEL.defaultMessage,
        isPodiumQuestionAnswerEnabled && PODIUM_QUESTION_ANSWER_LABEL.defaultMessage,
        isPodiumChatEnabled && PODIUM_CHAT_LABEL.defaultMessage,
    ].filter(Boolean).join(', ');

    return {
        name: 'features',
        value: features
    };
};

function assignEventBlueprintPropsToFields(template: IEventBlueprintDTO, fields: TTemplateFields): void {
    [
        mapIntegrationEnabledPropToField,
        mapEventTypesPropToField,
        mapAttendeeLinkTypePropToField,
        mapDefaultMeetingBlueprintIdPropToField,
    ].forEach(mapper => {
        const field = mapper(template);
        // @ts-ignore
        if (field) fields.push(field);
    });
};

const mapIntegrationEnabledPropToField = (template: IEventBlueprintDTO): Partial<BlueprintField> => ({
    name: 'integrationEnabled', value: template.integrationEnabled
});

const mapDefaultMeetingBlueprintIdPropToField = (template: IEventBlueprintDTO): Partial<BlueprintField> => ({
    name: 'defaultMeetingBlueprintId', value: template?.defaultMeetingBlueprintId
});

const mapEventTypesPropToField = (template: IEventBlueprintDTO): Partial<BlueprintField> | null => {
    return template.eventTypes.length ? { name: 'eventTypes', value: template.eventTypes.join(', ') } : null;
};

const mapAttendeeLinkTypePropToField = (template: IEventBlueprintDTO): Partial<BlueprintField> | null => {
    const reactIntlProvider = IoC.Container.get(IReactIntlProvider);
    const intl = reactIntlProvider.getIntl();

    if (template.integrationEnabled && template.attendeeLinkType) {
        const option = attendeeLinkTypeOptions(intl).find(opt => opt.value === template.attendeeLinkType);
        return { name: 'attendeeLinkType', value: option.label };
    } else {
        return null;
    }
};

export const mapConnectionFields = (connections: TConnection[]): TObject<string | number>[] => {
    return connections.map(x => {
        switch (x.type) {
            case ConnectionType.Zoom:
            case ConnectionType.Webinar:
                return composeZoomWebinarConnectionProp(x as IZoomConnection);
            case ConnectionType.Knovio:
                return composeKnovioConnectionProp(x as IKnovioConnection);
            case ConnectionType.Other:
                return composeOtherConnectionProp(x as IOtherConnection);
            case ConnectionType.AudioBridge:
                return composeAudioBridgeConnectionProp(x as IAudioBridgeConnection);
        }
    });
};

const composeZoomWebinarConnectionProp = (connection: IZoomConnection | IZoomWebinarConnection): TObject<string | number> => {
    const autoRegEnabled = connection.autoRegistrationEnabled && 'Auto Reg';
    const autoSyncEnabled = connection.autoSyncEnabled && 'Auto Sync';
    const registrationDisabled = !connection.registrationEnabled && 'Registration Disabled';
    const registrationEnabled = [
        connection.registrationEnabled && 'Registration Enabled',
        approvalTypes[connection.approvalType],
        connection.registrantsConfirmationEmail ? 'Zoom Emails Enabled' : 'Zoom Emails Disabled',
    ].filter(Boolean).join(', ');

    let settings = autoRegEnabled || autoSyncEnabled || registrationDisabled || registrationEnabled;

    if (connection.type === ConnectionType.Webinar) {
        const webinarConnection = connection as IZoomWebinarConnection;
        const autoRecordSetting = webinarConnection.autoRecording === WebinarAutoRecording.Cloud
            ? 'Automatically Record Enabled'
            : 'Automatically Record Disabled';

        settings = `${autoRecordSetting}, ${settings}`;

        if (webinarConnection.panelistLinkCreationEnabled) {
            settings = settings ? `${settings}, Panelist Link Creation Enabled` : 'Panelist Link Creation Enabled';
        }
    }

    return {
        title: connection.type,
        label: connection.label,
        uniqueReference: connection.meetingUniqueReference,
        group: connection.zoomGroupId,
        settings: settings
    };
};

const composeKnovioConnectionProp = (connection: IKnovioConnection): TObject<string | number> => {
    const reactIntlProvider = IoC.Container.get(IReactIntlProvider).getIntl();
    const expirationDateField = connection.enableExpirationDate
        ? reactIntlProvider.formatMessage(DAYS_COUNT_BEFORE_EXPIRATION_TEXT, { prefix: connection.expirationDays })
        : EnablementStatus.DISABLED_UPPERCASE;

    return {
        title: KnovioLabel.OEStream,
        label: connection.label,
        neverlate: isTrackingFieldEnabled(connection.enableNeverlate),
        flyoutTabs: [
            connection.enableOverview && 'Overview' || '',
            connection.enableQAndA && 'Q&A' || '',
            connection.enableSpeakers && 'Speakers' || '',
        ].filter(Boolean).join(', '),
        standByMessage: connection.standByMessage,
        published: isTrackingFieldEnabled(connection.publishedEnabled),
        passportReplay: isTrackingFieldEnabled(connection.passportReplayEnabled),
        expirationDate: expirationDateField
    };
};

const composeOtherConnectionProp = (connection: IOtherConnection): TObject<string | number> => {
    return {
        title: connection.type,
        name: connection.name,
        uniqueReference: connection.meetingUniqueReference,
        connectionLink: connection.connectionLink,
    };
};

const composeAudioBridgeConnectionProp = (connection: IAudioBridgeConnection): TObject<string | number> => {
    const autoSyncEnabled = connection.autoSyncEnabled && 'Auto Sync';
    const autoRecordSetting = connection.recordCalls
        ? 'Automatically Record Enabled'
        : 'Automatically Record Disabled';

    return {
        title: connection.type,
        label: connection.label,
        uniqueReference: connection.meetingUniqueReference,
        conferenceMode: ConfernceModeValues[connection.conferenceMode],
        settings: [autoRecordSetting, autoSyncEnabled].filter(Boolean).join(', ')
    };
};

function assignSingleMeetingCustomPagePropsToFields(template: IMeetingTemplateDTO, fields: TTemplateFields): void {
    const fieldNamesToAssign = ['pageLinkEnabled', 'landingPage', 'disableOtherPages'];
    const hasSingleMeetingCustomPageAction = !!template.actions?.find(action => action.type === ActionType.SINGLE_MEETING_CUSTOM_PAGE);

    if (hasSingleMeetingCustomPageAction) {
        fieldNamesToAssign.forEach(key => {
            const fieldExists = !!template.fields.find(field => field.name === key);
            if (!fieldExists) {
                // @ts-ignore
                fields.push({ name: key, value: false, type: ActionType.SINGLE_MEETING_CUSTOM_PAGE });
            }
        });
    }
}

function mapRoleConnectionFields(
    roleConnections: IRoleConnectionInput[],
    connections: TConnection[],
    connectionGroups: IConnectionTemplateConnectionGroupDTO[]
): IRoleConnectionDetail[] {
    const labels = {
        presenter: 'Presenter',
        host: 'Host',
        support: 'Support',
    };
    const assignments = fromRoleConnectionFieldValue(roleConnections);
    return (Object.keys(labels) as (keyof typeof labels)[]).flatMap(key => {
        const assignment = assignments[key];
        if (!assignment) return [];
        const name = resolveRoleConnectionLabel(assignment, connections || [], connectionGroups || []);
        if (!name) return [];
        return [{ role: labels[key], name: name }];
    });
}

function mapConnectionGroupsFields(
    connectionGroups: IConnectionTemplateConnectionGroupDTO[],
    connections: TConnection[]
): TConnectionGroupDetails[] {
    return connectionGroups.map(group => {
        return {
            label: group.label,
            connectionsLabels: group.connections.map(c => {
                // @ts-ignore
                const connection = connections.find(con => (con.customConnectionId || con.connectionId) === c.connectionId);
                return getConnectionTemplateItemLabelText(connection);
            })
        };
    });
};
